import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Link2,
  Plus,
  Trash2,
  ArrowUp,
  ArrowDown,
  ExternalLink,
  MousePointerClick,
  Save,
} from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import { api } from './api';

const ICON_CHOICES = [
  { value: 'calendar', label: 'Calendar (consult)' },
  { value: 'book', label: 'Book (playbook)' },
  { value: 'sparkles', label: 'Sparkles (quiz)' },
  { value: 'globe', label: 'Globe (website)' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'mail', label: 'Mail (newsletter)' },
  { value: 'mic', label: 'Mic (podcast)' },
  { value: 'youtube', label: 'YouTube' },
  { value: 'target', label: 'Target (assessment / IDP)' },
  { value: 'link', label: 'Generic link' },
];

const DEFAULT_NEW = {
  title: '',
  subtitle: '',
  url: '',
  icon: 'link',
  order: 100,
  active: true,
};

// Inline editor row — kept local so we don’t bloat the parent component.
function LinkRow({ link, onSave, onDelete, onMoveUp, onMoveDown, isFirst, isLast }) {
  const [draft, setDraft] = useState(link);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);

  // Keep local draft in sync when parent refreshes (e.g. after reorder).
  useEffect(() => {
    setDraft(link);
    setDirty(false);
  }, [link]);

  const update = (field, value) => {
    setDraft((d) => ({ ...d, [field]: value }));
    setDirty(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      await onSave(link.id, {
        title: draft.title,
        subtitle: draft.subtitle,
        url: draft.url,
        icon: draft.icon,
        active: draft.active,
      });
      setDirty(false);
      toast.success('Saved.');
    } catch (err) {
      logger.error('bio-hub save failed', err);
      toast.error(err.message || 'Could not save changes.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete "${link.title}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await onDelete(link.id);
      toast.success('Link deleted.');
    } catch (err) {
      logger.error('bio-hub delete failed', err);
      toast.error(err.message || 'Could not delete.');
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-xl border border-border bg-white p-4 sm:p-5 space-y-3"
      data-testid={`biohub-admin-row-${link.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
            #{link.order}
          </Badge>
          {!draft.active && (
            <Badge
              variant="outline"
              className="text-[10px] uppercase tracking-wider text-amber-700 border-amber-400"
            >
              Hidden
            </Badge>
          )}
          <span className="text-[12px] text-foreground/60 inline-flex items-center gap-1">
            <MousePointerClick className="h-3.5 w-3.5" />
            {link.click_count ?? 0} clicks
          </span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={isFirst || busy}
            onClick={() => onMoveUp(link)}
            aria-label="Move up"
            data-testid={`biohub-admin-up-${link.id}`}
          >
            <ArrowUp className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={isLast || busy}
            onClick={() => onMoveDown(link)}
            aria-label="Move down"
            data-testid={`biohub-admin-down-${link.id}`}
          >
            <ArrowDown className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={remove}
            disabled={busy}
            aria-label="Delete"
            data-testid={`biohub-admin-delete-${link.id}`}
          >
            <Trash2 className="h-4 w-4 text-red-600" />
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-foreground/60">
            Title
          </Label>
          <Input
            value={draft.title}
            onChange={(e) => update('title', e.target.value)}
            placeholder="Book a Discovery Call"
            className="mt-1"
            data-testid={`biohub-admin-title-${link.id}`}
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-foreground/60">
            Subtitle
          </Label>
          <Input
            value={draft.subtitle || ''}
            onChange={(e) => update('subtitle', e.target.value)}
            placeholder="Short description shown under the title"
            className="mt-1"
            data-testid={`biohub-admin-subtitle-${link.id}`}
          />
        </div>
      </div>

      <div className="grid sm:grid-cols-[1fr_220px] gap-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-foreground/60">
            URL
          </Label>
          <Input
            value={draft.url}
            onChange={(e) => update('url', e.target.value)}
            placeholder="https://... or /quiz"
            className="mt-1"
            data-testid={`biohub-admin-url-${link.id}`}
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-foreground/60">
            Icon
          </Label>
          <Select
            value={draft.icon || 'link'}
            onValueChange={(v) => update('icon', v)}
          >
            <SelectTrigger
              className="mt-1"
              data-testid={`biohub-admin-icon-${link.id}`}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ICON_CHOICES.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
        <label className="inline-flex items-center gap-2 text-[13px] font-medium text-foreground/80">
          <Switch
            checked={!!draft.active}
            onCheckedChange={(v) => update('active', v)}
            data-testid={`biohub-admin-active-${link.id}`}
          />
          {draft.active ? 'Active (visible to public)' : 'Hidden'}
        </label>
        <div className="flex items-center gap-2">
          {draft.url && (
            <a
              href={draft.url.startsWith('http') ? draft.url : draft.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-foreground/70 hover:text-foreground"
              data-testid={`biohub-admin-preview-${link.id}`}
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Preview
            </a>
          )}
          <Button
            type="button"
            onClick={save}
            disabled={!dirty || busy || !draft.title.trim() || !draft.url.trim()}
            className="h-9"
            data-testid={`biohub-admin-save-${link.id}`}
          >
            <Save className="h-4 w-4 mr-1.5" />
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function AdminBioHub() {
  const [links, setLinks] = useState(null);
  const [totalClicks, setTotalClicks] = useState(0);
  const [newLink, setNewLink] = useState(DEFAULT_NEW);
  const [creating, setCreating] = useState(false);

  const refresh = async () => {
    try {
      const body = await api.get('/api/admin/bio-hub/links');
      setLinks(body?.links || []);
      setTotalClicks(body?.total_clicks || 0);
    } catch (err) {
      logger.error('bio-hub admin load failed', err);
      toast.error(err.message || 'Could not load Bio Hub links.');
      setLinks([]);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    const title = newLink.title.trim();
    const url = newLink.url.trim();
    if (!title || !url) {
      toast.error('Title and URL are required.');
      return;
    }
    setCreating(true);
    try {
      // Place new links at the bottom by default.
      const maxOrder = (links || []).reduce(
        (m, l) => Math.max(m, l.order || 0),
        0,
      );
      await api.post('/api/admin/bio-hub/links', {
        title,
        url,
        subtitle: newLink.subtitle.trim(),
        icon: newLink.icon,
        order: maxOrder + 10,
        active: newLink.active,
      });
      setNewLink(DEFAULT_NEW);
      await refresh();
      toast.success('Link added.');
    } catch (err) {
      logger.error('bio-hub create failed', err);
      toast.error(err.message || 'Could not create link.');
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async (linkId, patch) => {
    await api.patch(`/api/admin/bio-hub/links/${linkId}`, patch);
    await refresh();
  };

  const handleDelete = async (linkId) => {
    await api.del(`/api/admin/bio-hub/links/${linkId}`);
    await refresh();
  };

  // Swap the `order` value of two adjacent links. We avoid persisting half a
  // swap by sending the requests sequentially and only refreshing once both
  // complete — if the second one fails we still surface the partial state.
  const reorderSwap = async (a, b) => {
    try {
      await api.patch(`/api/admin/bio-hub/links/${a.id}`, { order: b.order });
      await api.patch(`/api/admin/bio-hub/links/${b.id}`, { order: a.order });
      await refresh();
    } catch (err) {
      logger.error('bio-hub reorder failed', err);
      toast.error(err.message || 'Could not reorder.');
      await refresh();
    }
  };

  const sorted = (links || []).slice().sort((a, b) => a.order - b.order);

  return (
    <div className="space-y-6" data-testid="admin-biohub">
      <Card className="border-border">
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-wrap items-center gap-3 justify-between mb-1">
            <div className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-foreground/70" />
              <h2 className="font-heading text-lg font-bold tracking-tight">
                Instagram Bio Hub
              </h2>
            </div>
            <a
              href="/links"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-foreground/70 hover:text-foreground"
              data-testid="biohub-admin-open-public"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              View public page (/links)
            </a>
          </div>
          <p className="text-[13px] text-foreground/65 leading-relaxed">
            One link for your Instagram bio. Drag-rank the order, hide a link
            temporarily without deleting it, and track which offers actually
            convert.
          </p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Badge variant="secondary" data-testid="biohub-admin-stat-total-clicks">
              {totalClicks} total clicks
            </Badge>
            <Badge variant="secondary" data-testid="biohub-admin-stat-count">
              {(links || []).length} links
            </Badge>
            <Badge variant="secondary" data-testid="biohub-admin-stat-active">
              {(links || []).filter((l) => l.active).length} active
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Add new link */}
      <Card className="border-border">
        <CardContent className="p-5 sm:p-6">
          <h3 className="font-heading text-[15px] font-bold mb-3 flex items-center gap-2">
            <Plus className="h-4 w-4" /> Add a new link
          </h3>
          <form
            onSubmit={handleCreate}
            className="grid sm:grid-cols-2 gap-3"
            data-testid="biohub-admin-new-form"
          >
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-foreground/60">
                Title
              </Label>
              <Input
                value={newLink.title}
                onChange={(e) => setNewLink((s) => ({ ...s, title: e.target.value }))}
                placeholder="Listen to the Podcast"
                className="mt-1"
                required
                data-testid="biohub-admin-new-title"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-foreground/60">
                Subtitle (optional)
              </Label>
              <Input
                value={newLink.subtitle}
                onChange={(e) => setNewLink((s) => ({ ...s, subtitle: e.target.value }))}
                placeholder="Weekly mental performance for athletes"
                className="mt-1"
                data-testid="biohub-admin-new-subtitle"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-foreground/60">
                URL
              </Label>
              <Input
                value={newLink.url}
                onChange={(e) => setNewLink((s) => ({ ...s, url: e.target.value }))}
                placeholder="https://... or /quiz"
                className="mt-1"
                required
                data-testid="biohub-admin-new-url"
              />
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-foreground/60">
                Icon
              </Label>
              <Select
                value={newLink.icon}
                onValueChange={(v) => setNewLink((s) => ({ ...s, icon: v }))}
              >
                <SelectTrigger className="mt-1" data-testid="biohub-admin-new-icon">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ICON_CHOICES.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2 flex items-center justify-between gap-3">
              <label className="inline-flex items-center gap-2 text-[13px] text-foreground/80">
                <Switch
                  checked={newLink.active}
                  onCheckedChange={(v) => setNewLink((s) => ({ ...s, active: v }))}
                  data-testid="biohub-admin-new-active"
                />
                Publish immediately
              </label>
              <Button
                type="submit"
                disabled={creating || !newLink.title.trim() || !newLink.url.trim()}
                data-testid="biohub-admin-new-submit"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                {creating ? 'Adding…' : 'Add link'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Existing links */}
      <div className="space-y-3">
        {links === null ? (
          <>
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </>
        ) : sorted.length === 0 ? (
          <Card className="border-border">
            <CardContent className="py-10 text-center text-foreground/60 text-[13.5px]">
              No links yet. Add your first one above.
            </CardContent>
          </Card>
        ) : (
          sorted.map((link, idx) => (
            <LinkRow
              key={link.id}
              link={link}
              isFirst={idx === 0}
              isLast={idx === sorted.length - 1}
              onSave={handleSave}
              onDelete={handleDelete}
              onMoveUp={() => reorderSwap(link, sorted[idx - 1])}
              onMoveDown={() => reorderSwap(link, sorted[idx + 1])}
            />
          ))
        )}
      </div>
    </div>
  );
}
